import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Download } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "banners", label: "Banners" },
  { id: "newsletter", label: "Newsletter" },
  { id: "waitlist", label: "Waitlist" },
];

export default function AdminMarketing() {
  const [tab, setTab] = useState("banners");
  return (
    <div className="space-y-5" data-testid="admin-marketing">
      <div>
        <span className="label-overline text-jow-gold">Reach</span>
        <h1 className="font-display text-3xl text-jow-navy mt-1">Marketing</h1>
      </div>
      <div className="inline-flex border border-jow-navy/15 bg-white">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest ${
              tab === t.id ? "bg-jow-navy text-jow-cream" : "text-jow-navy/60"
            }`}
            data-testid={`mkt-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "banners" && <Banners />}
      {tab === "newsletter" && <Newsletter />}
      {tab === "waitlist" && <Waitlist />}
    </div>
  );
}

function Banners() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/banners").then((r) => setItems(r.data.items || []));
  useEffect(() => {
    load();
  }, []);
  const toggle = async (b) => {
    await api.patch(`/admin/banners/${b.slug}`, { is_active: !b.is_active });
    load();
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.slug}?`)) return;
    await api.delete(`/admin/banners/${b.slug}`);
    load();
  };

  return (
    <div className="space-y-3" data-testid="mkt-banners">
      <div className="flex justify-between">
        <span className="text-xs text-jow-navy/55">{items.length} banners</span>
        <button
          onClick={() => setEditing({})}
          className="btn-primary-jow text-[11px]"
          data-testid="new-banner-btn"
        >
          <Plus size={13} /> New banner
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((b) => (
          <div
            key={b.slug}
            className="bg-white border border-jow-navy/10 p-4 flex gap-3"
            data-testid={`banner-${b.slug}`}
          >
            {b.image_url && (
              <img src={b.image_url} alt="" className="w-24 h-24 object-cover" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-display text-base text-jow-navy">{b.title}</p>
              <p className="text-[10px] text-jow-navy/50">
                {b.slug} · sort {b.sort_order}
              </p>
              <p className="text-xs text-jow-navy/70 mt-1 truncate">{b.subtitle}</p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => toggle(b)}
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                    b.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                  }`}
                  data-testid={`banner-toggle-${b.slug}`}
                >
                  {b.is_active ? "on" : "off"}
                </button>
                <button onClick={() => setEditing(b)} className="text-jow-pink" data-testid={`banner-edit-${b.slug}`}>
                  <Edit size={13} />
                </button>
                <button onClick={() => remove(b)} className="text-jow-navy/40">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <BannerDrawer
          banner={editing.slug ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function BannerDrawer({ banner, onClose, onSaved }) {
  const [form, setForm] = useState(
    banner || {
      slug: "",
      title: "",
      subtitle: "",
      image_url: "",
      cta_text: "",
      cta_url: "",
      sort_order: 0,
      is_active: true,
    }
  );
  const save = async () => {
    try {
      if (banner) {
        const body = { ...form };
        delete body.slug;
        await api.patch(`/admin/banners/${banner.slug}`, body);
      } else {
        await api.post("/admin/banners", form);
      }
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };
  return (
    <div
      className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
      onClick={onClose}
      data-testid="banner-drawer"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl text-jow-navy">
            {banner ? `Edit ${banner.slug}` : "New banner"}
          </h2>
          <button onClick={onClose} className="text-jow-navy/50">
            <X size={16} />
          </button>
        </div>
        {!banner && (
          <FInput label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
        )}
        <FInput label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
        <FInput
          label="Subtitle"
          value={form.subtitle || ""}
          onChange={(v) => setForm({ ...form, subtitle: v })}
        />
        <FInput
          label="Image URL"
          value={form.image_url}
          onChange={(v) => setForm({ ...form, image_url: v })}
        />
        <div className="grid grid-cols-2 gap-3">
          <FInput
            label="CTA text"
            value={form.cta_text || ""}
            onChange={(v) => setForm({ ...form, cta_text: v })}
          />
          <FInput
            label="CTA URL"
            value={form.cta_url || ""}
            onChange={(v) => setForm({ ...form, cta_url: v })}
          />
          <FInput
            label="Sort order"
            type="number"
            value={form.sort_order}
            onChange={(v) => setForm({ ...form, sort_order: Number(v) })}
          />
        </div>
        <button onClick={save} className="btn-primary-jow w-full" data-testid="banner-save">
          Save
        </button>
      </aside>
    </div>
  );
}

function Newsletter() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/newsletter").then((r) => setItems(r.data.items || []));
  }, []);
  const exportCsv = () => {
    const token = localStorage.getItem("jow_token");
    const url = `${API}/admin/newsletter?export=csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => downloadBlob(b, "newsletter.csv"));
  };
  return (
    <div className="space-y-3" data-testid="mkt-newsletter">
      <div className="flex justify-between">
        <span className="text-xs text-jow-navy/55">{items.length} subscribers</span>
        <button
          onClick={exportCsv}
          className="btn-secondary-jow text-[11px]"
          data-testid="nl-export"
        >
          <Download size={13} /> Export CSV
        </button>
      </div>
      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Subscribed</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s, i) => (
            <tr key={i} className="border-t border-jow-navy/5">
              <td className="px-4 py-2">{s.email}</td>
              <td className="px-4 py-2 text-xs text-jow-navy/55">
                {new Date(s.subscribed_at).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Waitlist() {
  const [pin, setPin] = useState("");
  const [data, setData] = useState({ items: [], top_pincodes: [] });
  useEffect(() => {
    const params = new URLSearchParams();
    if (pin) params.set("pincode", pin);
    api.get(`/admin/waitlist?${params.toString()}`).then((r) => setData(r.data));
  }, [pin]);
  const exportCsv = () => {
    const token = localStorage.getItem("jow_token");
    const url = `${API}/admin/waitlist?export=csv${pin ? `&pincode=${pin}` : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => downloadBlob(b, `waitlist${pin ? "-" + pin : ""}.csv`));
  };
  return (
    <div className="space-y-3" data-testid="mkt-waitlist">
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Filter by pincode"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          className="border border-jow-navy/15 px-3 py-2 text-sm"
          data-testid="wl-pin"
        />
        <button
          onClick={exportCsv}
          className="btn-secondary-jow text-[11px]"
          data-testid="wl-export"
        >
          <Download size={13} /> Export
        </button>
      </div>
      {data.top_pincodes.length > 0 && !pin && (
        <div className="bg-white border border-jow-navy/10 p-4">
          <p className="label-overline text-jow-navy mb-3">Top pincodes</p>
          <div className="grid grid-cols-5 gap-2">
            {data.top_pincodes.map((p) => (
              <button
                key={p.pincode}
                onClick={() => setPin(p.pincode)}
                className="bg-jow-cream/60 border border-jow-navy/10 px-3 py-2 text-left hover:border-jow-gold"
                data-testid={`wl-top-${p.pincode}`}
              >
                <p className="font-display text-jow-navy">{p.pincode}</p>
                <p className="text-[10px] uppercase tracking-widest text-jow-navy/50">
                  {p.count} folks
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 uppercase tracking-widest text-[10px] text-jow-navy/70">
          <tr>
            <th className="text-left px-4 py-3">Pincode</th>
            <th className="text-left px-4 py-3">Phone</th>
            <th className="text-left px-4 py-3">Email</th>
            <th className="text-left px-4 py-3">Name</th>
            <th className="text-left px-4 py-3">When</th>
          </tr>
        </thead>
        <tbody>
          {data.items.slice(0, 100).map((e, i) => (
            <tr key={i} className="border-t border-jow-navy/5">
              <td className="px-4 py-2 font-mono text-xs">{e.pincode}</td>
              <td className="px-4 py-2 text-jow-navy/70">{e.phone || "—"}</td>
              <td className="px-4 py-2 text-jow-navy/70">{e.email || "—"}</td>
              <td className="px-4 py-2">{e.name || "—"}</td>
              <td className="px-4 py-2 text-xs text-jow-navy/50">
                {new Date(e.created_at).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`Downloaded ${name}`);
}

function FInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-jow-navy/15 px-3 py-2 text-sm bg-white"
      />
    </div>
  );
}
